import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Search, Loader2, ChevronLeft, ChevronRight, Trash, Pencil } from "lucide-react";
import { formatCurrencyBRL, getTipoAceiteLabel, getTipoAceiteClass } from "../../../lib/utils";

export function CedentesList({
    cedentes, loading, totalItems, page, setPage, pageSize, setPageSize,
    onUnlink, onBulkUnlink, onEdit, onSearch, searchQuery, isDebouncing
}) {
  const totalPages = Math.ceil(totalItems / pageSize);
  const [selectedIds, setSelectedIds] = React.useState([]);

  // Reset selections when data changes
  React.useEffect(() => {
     setSelectedIds([]);
  }, [cedentes]);

  const handleSelectAll = (e) => {
      if (e.target.checked) {
          setSelectedIds(cedentes.map(pc => pc.cedente_id));
      } else {
          setSelectedIds([]);
      }
  };

  const handleSelectOne = (e, cedenteId) => {
      if (e.target.checked) {
          setSelectedIds(prev => [...prev, cedenteId]);
      } else {
          setSelectedIds(prev => prev.filter(id => id !== cedenteId));
      }
  };

  const handleBulkDelete = () => {
      if (selectedIds.length === 0) return;
      onBulkUnlink(selectedIds);
  };

  const isAllSelected = cedentes.length > 0 && selectedIds.length === cedentes.length;

  return (
    <>
      <div className="mb-4 relative flex justify-between items-center">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input 
                placeholder="Buscar substituídos vinculados..."
                className="pl-8 w-full"
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
            />
            {isDebouncing && (
                <div className="absolute right-3 top-2.5 flex items-center text-xs text-slate-400 gap-1 animate-pulse">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Buscando...
                </div>
            )}
          </div>
          
          {selectedIds.length > 0 && (
              <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash className="h-4 w-4 mr-2" />
                  Excluir Selecionados ({selectedIds.length})
              </Button>
          )}
      </div>
      
      {loading ? (
          <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
      ) : (
        <>
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                <TableRow>
                    <TableHead className="w-[50px]">
                        <input 
                            type="checkbox" 
                            className="rounded border-gray-300"
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                        />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>CPF</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Valor Prop.</TableHead>
                    <TableHead>Valor Parcelado</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                </TableRow>
                </TableHeader>
                <TableBody>
                {cedentes.map(pc => (
                    <TableRow key={pc.id}>
                    <TableCell>
                        <input 
                            type="checkbox" 
                            className="rounded border-gray-300"
                            checked={selectedIds.includes(pc.cedente_id)}
                            onChange={(e) => handleSelectOne(e, pc.cedente_id)}
                        />
                    </TableCell>
                    <TableCell>{pc.cedente?.nome}</TableCell>
                    <TableCell>{pc.cedente?.cpf}</TableCell>
                    <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getTipoAceiteClass(pc.tipo_aceite)}`}>
                            {getTipoAceiteLabel(pc.tipo_aceite)}
                        </span>
                    </TableCell>
                    <TableCell>
                        {pc.valor_proposta_final ? formatCurrencyBRL(pc.valor_proposta_final) : '-'}
                    </TableCell>
                    <TableCell>
                        {pc.valor_parcelado ? `R$ ${pc.valor_parcelado.toLocaleString('pt-BR')}` : '-'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => onEdit(pc)}>
                            <Pencil className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onUnlink(pc.cedente_id)}>
                            <Trash className="h-4 w-4 text-red-500" />
                        </Button>
                    </TableCell>
                    </TableRow>
                ))}
                {cedentes.length === 0 && (
                    <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500">Nenhum substituído vinculado</TableCell>
                    </TableRow>
                )}
                </TableBody>
            </Table>
        </div>
        
        <div className="flex items-center justify-between space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground flex items-center space-x-2">
                <span>Total: {totalItems}</span>
                {setPageSize && (
                    <select
                        className="ml-4 h-8 w-20 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1); // Reset to first page
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={totalItems || 1000}>Tudo</option>
                    </select>
                )}
            </div>
            <div className="space-x-2">
                <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                >
                <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0 || loading}
                >
                Próximo <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
        </>
      )}
    </>
  );
}
